"use client";

import { useEffect, useRef, useState, type DragEvent } from "react";
import { ImagePlus, Paperclip, UploadCloud, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { MediaMosaic } from "@/components/ui/media-mosaic";
import { cn, formatFileSize } from "@/lib/utils";

type FileDropzoneProps = {
  id?: string;
  accept?: string;
  multiple?: boolean;
  files: File[];
  onFilesChange: (files: File[]) => void;
  title?: string;
  description?: string;
  compact?: boolean;
  imageOnly?: boolean;
  disabled?: boolean;
  className?: string;
};

export function FileDropzone({
  id,
  accept,
  multiple = false,
  files,
  onFilesChange,
  title = "Húzza ide a fájlt",
  description = "vagy válassza ki a számítógépéről",
  compact = false,
  imageOnly = false,
  disabled = false,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function addFiles(fileList: FileList | null) {
    if (!fileList || disabled) {
      return;
    }

    const selected = Array.from(fileList);
    onFilesChange(multiple ? [...files, ...selected] : selected.slice(0, 1));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div
        className={cn(
          "group flex cursor-pointer items-center rounded-xl border border-dashed bg-muted/30 transition-colors",
          compact ? "gap-3 px-3 py-2.5" : "flex-col justify-center px-5 py-6 text-center",
          dragging
            ? "border-primary bg-primary/5"
            : "hover:border-primary/50 hover:bg-primary/[0.03]",
          disabled && "pointer-events-none opacity-50",
        )}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary",
            compact ? "size-9" : "mb-3 size-11",
          )}
        >
          {imageOnly ? (
            <ImagePlus className="size-5" />
          ) : compact ? (
            <Paperclip className="size-4" />
          ) : (
            <UploadCloud className="size-5" />
          )}
        </span>
        <span className={cn(compact && "min-w-0 flex-1 text-left")}>
          <span className="block text-sm font-medium">{title}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {description}
          </span>
        </span>
        {compact ? (
          <span className="text-xs font-semibold text-primary">Tallózás</span>
        ) : (
          <span className="mt-3 rounded-full border bg-card px-3 py-1.5 text-xs font-semibold text-primary shadow-sm">
            Fájl tallózása
          </span>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="sr-only"
          onChange={(event) => addFiles(event.target.files)}
        />
      </div>

      {files.length > 0 ? (
        imageOnly ? (
          <MediaPreviewList
            files={files}
            onRemove={(index) =>
              onFilesChange(files.filter((_, itemIndex) => itemIndex !== index))
            }
          />
        ) : (
        <ul className="space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2"
            >
              <Paperclip className="size-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatFileSize(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`${file.name} eltávolítása`}
                onClick={(event) => {
                  event.stopPropagation();
                  onFilesChange(files.filter((_, itemIndex) => itemIndex !== index));
                }}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
        )
      ) : null}
    </div>
  );
}

function MediaPreviewList({
  files,
  onRemove,
}: {
  files: File[];
  onRemove: (index: number) => void;
}) {
  const fileKey = files
    .map((file) => `${file.name}-${file.size}-${file.lastModified}`)
    .join("|");
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const next = files.map((file) => URL.createObjectURL(file));
    setUrls(next);
    return () => {
      next.forEach((url) => URL.revokeObjectURL(url));
    };
    // fileKey captures the selected files without recreating previews on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  return (
    <MediaMosaic
      className="border"
      items={files.map((file, index) => ({
        key: `${file.name}-${file.lastModified}-${index}`,
        src: urls[index] ?? "",
        alt: file.name,
        kind: file.type.startsWith("video/") ? "video" : "image",
      }))}
      overlay={(item, index) => (
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="absolute top-2 right-2 size-7 rounded-full bg-white/90 shadow-sm"
          aria-label={`${item.alt ?? "Fájl"} eltávolítása`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
        >
          <X className="size-3.5" />
        </Button>
      )}
    />
  );
}
